import useflowsProductBasketStore, {
  Package,
} from '../store/zustand/flows-products/flowsProductBasketStore';

/**
 * Calculates the total price of all standalone insurance quotes
 * @returns The sum of total_premium for all insurance quotes
 */
export const getTotalInsurancesPrice = (): number => {
  const { insurances } = useflowsProductBasketStore.getState();
  return insurances.reduce((total, quote) => total + quote.total_premium, 0);
};

/**
 * Calculates the total price of all quotes within packages
 * @returns The sum of total_premium for all quotes in packages
 */
export const getTotalPackagesPrice = (): number => {
  const { packages } = useflowsProductBasketStore.getState();
  return packages.reduce((total, pkg) => {
    const packageTotal = pkg.quotes.reduce(
      (subtotal, quote) => subtotal + quote.total_premium,
      0,
    );
    return total + packageTotal;
  }, 0);
};

/**
 * Calculate the applicable discount percentage for a package based on the number of quotes
 * @param pkg The package to calculate discount for
 * @returns The applicable discount percentage (e.g., 0.1 for 10%)
 */
export const getApplicableDiscount = (pkg: Package): number => {
  if (
    !pkg.discounts ||
    pkg.discounts.length === 0 ||
    pkg.excluded_from_discounts
  ) {
    return 0;
  }

  // Sort discounts by amount_of_policies in descending order to get the highest applicable discount first
  const sortedDiscounts = [...pkg.discounts].sort(
    (a, b) => b.minimum_amount_of_policies - a.minimum_amount_of_policies,
  );

  // Find the highest discount tier that applies based on quote count
  const quoteCount = pkg.quotes.length;
  const applicableDiscount = sortedDiscounts.find(
    (discount) => quoteCount >= discount.minimum_amount_of_policies,
  );

  // Return the percentage as decimal (e.g., 10% becomes 0.1)
  return applicableDiscount ? applicableDiscount.percentage / 100 : 0;
};

/**
 * Calculates the total discount amount across all packages
 * @returns The sum of discount amounts for all packages
 */
export const getTotalDiscount = (): number => {
  const { packages } = useflowsProductBasketStore.getState();
  return packages.reduce((total, pkg) => {
    // Calculate raw sum of quotes in this package
    const quoteSum = pkg.quotes.reduce(
      (subtotal, quote) => subtotal + quote.total_premium,
      0,
    );

    // Calculate discount amount based on package discount percentage
    const discountPercentage = getApplicableDiscount(pkg);

    // Return cumulative discount amount
    return total + quoteSum * discountPercentage;
  }, 0);
};

/**
 * Calculates the grand total (insurances + packages - discount)
 * @returns The grand total amount
 */
export const getGrandTotal = (): number => {
  return (
    getTotalInsurancesPrice() + getTotalPackagesPrice() - getTotalDiscount()
  );
};
